import { useState, type FormEvent, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import {
  IconArrowRight,
  IconClock,
  IconMail,
  IconSparkle,
} from '@/components/home/icons';

const CONTACT_EMAIL = 'ehsaasradio@gmail.com';
const SUBJECTS = [
  'General question',
  'Feedback',
  'Report a problem',
  'Qari program',
  'Privacy request',
];

const FIELD =
  'w-full rounded-lg border border-night-500/50 bg-night-800/70 px-4 py-2.5 ' +
  'text-sm text-night-50 placeholder:text-night-300 transition-colors ' +
  'focus:border-beam focus:outline-none';

type Status = 'idle' | 'sending' | 'sent' | 'error';

/** Contact page — a form that delivers via the /api/contact function. */
export function Contact() {
  useDocumentTitle('Contact');
  const [status, setStatus] = useState<Status>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);
    const payload = {
      name: String(data.get('name') ?? ''),
      email: String(data.get('email') ?? ''),
      subject: String(data.get('subject') ?? ''),
      message: String(data.get('message') ?? ''),
      company: String(data.get('company') ?? ''), // honeypot
    };

    setStatus('sending');
    setErrorMsg('');
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = (await res.json().catch(() => null)) as
        | { ok: boolean; error?: { message?: string } }
        | null;
      if (res.ok && body?.ok) {
        form.reset();
        setStatus('sent');
      } else {
        setStatus('error');
        setErrorMsg(
          body?.error?.message ??
            'Something went wrong sending your message. Please try again.',
        );
      }
    } catch {
      setStatus('error');
      setErrorMsg(
        'Could not reach the server. Please try again, or email us directly.',
      );
    }
  }

  return (
    <section className="container-wide pb-24 pt-12 sm:pt-16">
      <header className="mb-10 max-w-2xl">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-beam">
          Contact
        </p>
        <h1 className="mt-3 font-display text-3xl font-extrabold tracking-tight text-night-50 sm:text-4xl">
          Get in touch
        </h1>
        <p className="mt-3 text-night-200">
          A question, feedback, a problem to report, or interest in the Qari
          program — send a message and we will get back to you.
        </p>
      </header>

      <div className="grid gap-8 lg:grid-cols-[1.35fr_1fr]">
        {/* Form */}
        <div className="rounded-2xl border border-night-500/40 bg-night-700/60 p-7 sm:p-8">
          {status === 'sent' ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-full border border-beam/40 bg-beam/10 text-beam">
                <IconMail className="h-6 w-6" />
              </span>
              <h2 className="mt-4 font-display text-xl font-bold text-night-50">
                Message sent
              </h2>
              <p className="mt-2 max-w-sm text-night-200">
                Thank you for reaching out. We have received your message and
                will reply by email soon.
              </p>
              <button
                type="button"
                onClick={() => setStatus('idle')}
                className="mt-6 text-sm font-semibold text-beam hover:underline"
              >
                Send another message
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} className="space-y-5">
              {/* Honeypot — hidden from people, tempting to bots. */}
              <input
                type="text"
                name="company"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                className="hidden"
              />

              <div className="grid gap-5 sm:grid-cols-2">
                <Field label="Your name" required>
                  <input
                    name="name"
                    type="text"
                    required
                    maxLength={120}
                    placeholder="Your name"
                    className={FIELD}
                  />
                </Field>
                <Field label="Email address" required>
                  <input
                    name="email"
                    type="email"
                    required
                    maxLength={200}
                    placeholder="you@example.com"
                    className={FIELD}
                  />
                </Field>
              </div>

              <Field label="Subject">
                <select name="subject" defaultValue={SUBJECTS[0]} className={FIELD}>
                  {SUBJECTS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Message" required>
                <textarea
                  name="message"
                  required
                  rows={6}
                  maxLength={5000}
                  placeholder="How can we help?"
                  className={`${FIELD} resize-y`}
                />
              </Field>

              {status === 'error' && (
                <p
                  role="alert"
                  className="rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-200"
                >
                  {errorMsg}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'sending'}
                className="inline-flex h-12 items-center justify-center gap-2 rounded-xl bg-beam px-7 text-sm font-semibold text-beam-ink transition-all hover:shadow-[0_0_24px_-4px_rgba(0,229,255,0.55)] disabled:opacity-60"
              >
                {status === 'sending' ? 'Sending…' : 'Send message'}
                {status !== 'sending' && <IconArrowRight className="h-4 w-4" />}
              </button>
            </form>
          )}
        </div>

        {/* Info */}
        <div className="space-y-4">
          <InfoCard
            icon={<IconMail className="h-5 w-5" />}
            title="Email us directly"
            body={
              <a
                href={`mailto:${CONTACT_EMAIL}`}
                className="text-beam hover:underline"
              >
                {CONTACT_EMAIL}
              </a>
            }
          />
          <InfoCard
            icon={<IconClock className="h-5 w-5" />}
            title="Response time"
            body="We usually reply within a few days."
          />
          <Link
            to="/ask"
            className="group block rounded-2xl border border-night-500/40 bg-night-700/60 p-6 transition-colors hover:border-beam/60"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-beam/30 bg-beam/10 text-beam">
              <IconSparkle className="h-5 w-5" />
            </span>
            <h3 className="mt-3 font-display text-sm font-bold text-night-50">
              Looking for an answer?
            </h3>
            <p className="mt-1 text-sm text-night-200">
              Ask a question about the Quran and get a cited answer instantly.
            </p>
            <span className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-beam">
              Open Ask
              <IconArrowRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-1" />
            </span>
          </Link>
        </div>
      </div>
    </section>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-sm font-medium text-night-100">
        {label}
        {required && <span className="text-beam"> *</span>}
      </span>
      {children}
    </label>
  );
}

function InfoCard({
  icon,
  title,
  body,
}: {
  icon: ReactNode;
  title: string;
  body: ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-night-500/40 bg-night-700/60 p-6">
      <span className="flex h-10 w-10 items-center justify-center rounded-lg border border-beam/30 bg-beam/10 text-beam">
        {icon}
      </span>
      <h3 className="mt-3 font-display text-sm font-bold text-night-50">
        {title}
      </h3>
      <div className="mt-1 text-sm text-night-200">{body}</div>
    </div>
  );
}
